import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GetRowIdParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { EMPTY, Subscription, concat, concatMap, from, of } from 'rxjs';
import {
  Company,
  CompanyVendor,
  License,
  PrimaryTrade,
  PrimaryTradeLicenseRequirement,
  ViveProgress
} from 'src/app/classes/models';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { VendorTrade } from 'src/app/classes/models/vendor-trade';
import { ComplianceReviewAction } from 'src/app/interfaces/compliance-review-action';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { EditLicenseModalComponent } from 'src/app/modals/edit-license-modal/edit-license-modal.component';
import { TradesModalComponent } from 'src/app/modals/trades-modal/trades-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceStepStatusService } from 'src/app/services/compliance-step-status.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { LicenseService } from 'src/app/services/license.service';
import { NoteService } from 'src/app/services/note.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeLicenseService } from 'src/app/services/trade-license.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorTradeService } from 'src/app/services/vendor-trade.service';

@Component({
  selector: 'app-compliance-trade-review',
  templateUrl: './compliance-trade-review.component.html',
  styleUrls: ['./compliance-trade-review.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceTradeReviewComponent implements OnInit {
  constructor(
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private CompanyVendorService: CompanyVendorService,
    private route: ActivatedRoute,
    private ModalService: NgbModal,
    private TradeLicenseService: TradeLicenseService,
    private VendorTradeService: VendorTradeService,
    private NoteService: NoteService,
    private AuthService: AuthService,
    private LicenseService: LicenseService,
    private UtilityService: UtilityService,
    private ComplianceService: ComplianceService,
    private TradeService: TradeService,
    private ComplianceStepStatusService: ComplianceStepStatusService
  ) {
    this.SharedDataServiceSubscription = this.SharedDataService.listenForEvent<
      ViveProgress | null | ComplianceReviewAction
    >().subscribe({
      next: ({ eventName, data }) => {
        if (
          eventName === 'SetReviewAction' &&
          data &&
          !(data instanceof ViveProgress)
        ) {
          this.action = data;
          this.SharedDataService.sendEvent(
            'GetComplianceProgress',
            new ViveProgress({ type: 'Trades & Licenses' })
          );
        }

        if (
          eventName === 'SetComplianceProgress' &&
          (data instanceof ViveProgress || data === null)
        ) {
          this.ComplianceProgress = data;

          if (this.ComplianceProgress) {
            const { subStatus } = this.ComplianceProgress;

            if (subStatus && subStatus === 'Waived Findings') {
              this.ToastService.addErrorToast(
                'Info',
                "Vendor's license was previously waived."
              );
            }
          }

          this.displayAutoApproveOrExpiringSoonModal();

          // Set compliance progress if running compliance review.
          if (this.action === 'review') {
            if (!this.ComplianceProgress) {
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({
                  status: 'Reviewing',
                  type: 'Trades & Licenses'
                })
              );
            } else {
              const { status } = this.ComplianceProgress;

              if (status === null) {
                this.SharedDataService.sendEvent(
                  'UpdateComplianceProgress',
                  new ViveProgress({
                    status: 'Reviewing',
                    type: 'Trades & Licenses'
                  })
                );
              }
            }
          }
        }

        if (eventName === 'CheckForPassedFindings') {
          this.SharedDataService.sendEvent(
            'RespondIfPassedFindings',
            this.checkForPassedFindings()
          );
        }
      }
    });
  }

  CompanyId!: number;
  VendorId!: number;
  CompanyVendorId!: number;
  Company!: Company;
  CompanyVendor!: CompanyVendor;
  CompanyComplianceTypeId!: number;
  country!: 'US' | 'CA';
  action!: ComplianceReviewAction;
  ComplianceProgress?: ViveProgress | null;
  SharedDataServiceSubscription!: Subscription;
  vendorCompanyTradeStates: String = '';

  // we capture an instance of the Auto Approve modal to ensure we only display 1 at a time.
  AutoApproveModal?: NgbModalRef;

  // Maps Primary Trade Id to Compliance Setting.
  PrimaryTradeIdToComplianceSettingMap = new Map<number, ComplianceSetting>([]);
  VendorTrades: VendorTrade[] = [];
  // A license can be bound to multiple requirements.
  LicenseIdToPrimaryTradeLicenseRequirementMap = new Map<
    number,
    Map<number, PrimaryTradeLicenseRequirement>
  >([]);

  SuppliedPrimaryTradeLicenseQueryData?: GenericObject;

  LicenseColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view', 'edit', 'archive']
  };

  TradesColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['delete']
  };

  TradesTooltipMap: {
    [key in Extract<GridActionColumnActions, 'delete'>]: string;
  } = {
    delete: 'Delete Trade'
  };

  LicenseTooltipMap: {
    [
      key in Extract<GridActionColumnActions, 'edit' | 'view' | 'archive'>
    ]: string;
  } = {
    edit: 'Edit License',
    view: 'View License Image',
    archive: 'Archive License'
  };

  TradesSuppliedGridQuery: GenericObject = {
    order: [['PrimaryTrade', 'name', 'ASC']]
  };

  reloadLicensedGrid: boolean = false;
  reloadVendorTradesGrid: boolean = false;
  LicensesMappedById = new Map<number, License>();
  LoggedInUser = this.AuthService.getUser();

  checkIfLicenseIsExpiringSoon() {
    const ExpirationResult = {
      isLicenseExpired: false,
      isLicenseExpiringSoon: false
    };

    Array.from(this.LicensesMappedById.values()).forEach((License) => {
      const { expirationDate } = License;
      if (expirationDate) {
        if (
          DateTime.fromJSDate(expirationDate).valueOf() <=
          DateTime.fromJSDate(new Date()).valueOf()
        ) {
          Object.assign(ExpirationResult, { isLicenseExpired: true });
        } else if (
          DateTime.fromJSDate(expirationDate).valueOf() <=
          DateTime.fromJSDate(new Date()).plus({ weeks: 2 }).valueOf()
        ) {
          Object.assign(ExpirationResult, { isLicenseExpiringSoon: true });
        }
      }
    });

    return ExpirationResult;
  }

  getLicensesRowId = (params: GetRowIdParams) =>
    String(params.data['Licenses.TradeLicense.id'] || params.data['id']);

  chooseTrades() {
    const { componentInstance, result }: NgbModalRef = this.ModalService.open(
      TradesModalComponent,
      { backdrop: 'static', modalDialogClass: 'trades-modal' }
    );

    Object.assign(componentInstance, {
      Companies: [this.Company],
      selectedTrades: this.VendorTrades.map((vt) => vt.PrimaryTrade)
    });

    result.then(
      (dataArray: Required<Pick<PrimaryTrade, 'id' | 'name'>>[] | null) => {
        if (dataArray) {
          const SelectPrimaryTradeIds = dataArray.map((pt) => pt.id);
          const VendorTradesToAdd: VendorTrade[] = [];

          SelectPrimaryTradeIds.forEach((id) => {
            VendorTradesToAdd.push(
              new VendorTrade({ VendorId: this.VendorId, PrimaryTradeId: id })
            );
          });

          this.VendorTradeService.saveVendorTrades(
            this.VendorId,
            VendorTradesToAdd
          ).subscribe({
            next: () => {
              this.reloadVendorTradesGrid = true;
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully updated vendor trades.'
              );
              this.loadData();
              this.reloadVendorTradesGrid = true;
            }
          });
        }
      }
    );
  }

  tradeActionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: VendorTrade;
  }) {
    if (action === 'delete') {
      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true, size: 'md' }
      );

      Object.assign(componentInstance, {
        displayText: "Are you sure you want to delete this vendor's trade?"
      });

      const { id } = rowData;

      if (!id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot delete vendor trade. No ID found.',
          { returnError: true }
        );
      }

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.VendorTradeService.deleteVendorTrade(id).subscribe({
              next: () => {
                this.reloadVendorTradesGrid = true;
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted vendor trade.'
                );
                this.loadData();
              }
            });
          }
        }
      });
    }
  }

  calculateLicenseGridActions({
    rowData
  }: {
    rowData: any;
  }): GridActionColumnActions[] | null {
    const gridActionColumnActionArr: GridActionColumnActions[] = ['edit'];
    const {
      ['Licenses.id']: Licenses_id,
      ['Licenses.status']: Licenses_status
    } = rowData;

    if (Licenses_id) {
      gridActionColumnActionArr.splice(1, 0, 'view');

      if (Licenses_status !== 'Archived') {
        gridActionColumnActionArr.push('archive');
      }
    }

    return gridActionColumnActionArr;
  }

  licenseActionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    const {
      ['id']: PrimaryTradeLicenseRequirementId,
      ['Licenses.id']: Licenses_id,
      ['VendorTrade.id']: VendorTrade_id,
      ['PrimaryTrade.id']: PrimaryTrade_id
    } = rowData;
    if (action === 'view') {
      if (!Licenses_id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Cannot view license image. No license image file found.',
          { returnError: true }
        );
      }

      this.viewUploadedLicense(Licenses_id);
    }

    if (action === 'edit') {
      if (!VendorTrade_id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Vendor trade information.',
          { returnError: true }
        );
      }

      const TradeRequirementQuery = {
        id: PrimaryTradeLicenseRequirementId,
        PrimaryTrade: {
          attributes: ['id', 'name']
        },
        SubNationalDivision: {
          attributes: ['id', 'abbreviation']
        },
        VendorTrade: {
          id: VendorTrade_id,
          VendorId: this.VendorId
        }
      };

      if (Licenses_id) {
        Object.assign(TradeRequirementQuery, {
          License: {
            attributes: ['id', 'VendorId', 'licenseFileName'],
            id: Licenses_id,
            VendorId: this.VendorId
          }
        });
      }

      this.TradeService.getTradeRequirement(
        this.UtilityService.stringifyObjectsInQueryObject(TradeRequirementQuery)
      ).subscribe({
        next: (PrimaryTradeLicenseRequirement) => {
          const { VendorTrade } = PrimaryTradeLicenseRequirement;

          if (!VendorTrade) {
            throw this.ToastService.addErrorToast(
              'Error',
              'There was an error loading Vendor trade information.',
              { returnError: true }
            );
          }

          const { id: VendorTradeId } = VendorTrade;

          (() => {
            if (Licenses_id) {
              return this.LicenseService.getLicense(
                this.UtilityService.stringifyObjectsInQueryObject({
                  id: Licenses_id,
                  status: {
                    $or: [
                      { $eq: 'Archived' },
                      { $ne: 'Archived' },
                      { $eq: null }
                    ]
                  }
                })
              );
            }

            return of<License | null>(null);
          })().subscribe({
            next: (License) => {
              if (License) {
                Object.assign(License, { PrimaryTradeId: PrimaryTrade_id });
              }

              const { componentInstance, result } = this.ModalService.open(
                EditLicenseModalComponent,
                { backdrop: 'static', modalDialogClass: 'edit-license-modal' }
              );

              Object.assign(componentInstance, {
                VendorId: this.VendorId,
                country: this.country,
                License,
                VendorTrade,
                PrimaryTradeLicenseRequirement
              });

              from(result).subscribe({
                next: (SavedLicense: License) => {
                  let TradeRequirement:
                    PrimaryTradeLicenseRequirement | undefined;
                  this.VendorTrades.find((VendorTrade) => {
                    const { PrimaryTradeLicenseRequirements } = VendorTrade;
                    TradeRequirement = (
                      PrimaryTradeLicenseRequirements || []
                    ).find(
                      (TradeRequirement) =>
                        TradeRequirement.id === PrimaryTradeLicenseRequirementId
                    );
                    return !!TradeRequirement;
                  });

                  const { id } = SavedLicense;

                  if (!id) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Saved license is missing id.',
                      { returnError: true }
                    );
                  }

                  this.LicensesMappedById.set(id, SavedLicense);

                  if (SavedLicense && TradeRequirement) {
                    const { id: LicenseId } = SavedLicense;
                    const { TradeLicenses } = TradeRequirement;
                    const NewTradeLicenses = TradeLicenses || [];
                    NewTradeLicenses.splice(0);
                    NewTradeLicenses.push({
                      LicenseId,
                      PrimaryTradeLicenseRequirementId,
                      VendorTradeId,
                      License: SavedLicense
                    });
                    Object.assign(TradeRequirement, {
                      TradeLicenses: NewTradeLicenses
                    });
                  }

                  this.reloadLicensedGrid = true;
                  this.displayAutoApproveOrExpiringSoonModal();
                },
                error: () => {
                  this.reloadLicensedGrid = true;
                }
              });
            }
          });
        }
      });
    }

    if (action === 'archive') {
      if (!Licenses_id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Trade License is missing License Id.',
          { returnError: true }
        );
      }

      if (!this.VendorId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor ID.', {
          returnError: true
        });
      }

      const license = new License({
        id: Licenses_id,
        status: 'Archived',
        VendorId: this.VendorId
      });

      const { componentInstance, result } = this.ModalService.open(
        ConfirmModalComponent,
        { backdrop: 'static', size: 'md' }
      );
      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to archive this license?'
      });

      from(result).subscribe({
        next: (confirm) => {
          if (confirm) {
            this.LicenseService.saveLicense(this.VendorId, license).subscribe({
              next: () => {
                this.LicensesMappedById.delete(Licenses_id);
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully archived license.'
                );
                this.reloadLicensedGrid = true;
              }
            });
          }
        }
      });
    }

    if (action === 'delete') {
      if (!VendorTrade_id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Trade License is missing Vendor Trade Id.',
          { returnError: true }
        );
      }

      if (!Licenses_id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Trade License is missing License Id.',
          { returnError: true }
        );
      }

      if (!PrimaryTradeLicenseRequirementId) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Trade License is missing Requirement Id.',
          { returnError: true }
        );
      }

      const ConfirmModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      const { componentInstance, result } = ConfirmModal;
      Object.assign(componentInstance, {
        displayText: 'Are you sure you want to delete this license?'
      });

      from(result).subscribe({
        next: (response) => {
          if (response) {
            this.TradeLicenseService.deleteTradeLicense(this.VendorId, {
              VendorTradeId: VendorTrade_id,
              LicenseId: Licenses_id,
              PrimaryTradeLicenseRequirementId
            }).subscribe({
              next: () => {
                this.LicensesMappedById.delete(Licenses_id);
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully deleted Trade License.'
                );
                this.reloadLicensedGrid = true;
              }
            });
          }
        }
      });
    }
  }

  viewUploadedLicense(id: number) {
    if (!id) {
      throw this.ToastService.addErrorToast('Error', 'License is missing Id.', {
        returnError: true
      });
    }

    this.LicenseService.getLicense(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'licenseFile', 'LicenseFileId'],
        status: {
          $or: [{ $eq: 'Archived' }, { $ne: 'Archived' }, { $eq: null }]
        },
        id
      })
    ).subscribe({
      next: (License: License) => {
        const { licenseFile, LicenseFileId } = License;

        if (LicenseFileId) {
          this.LicenseService.getLicenseFileS3PresignedUrl(
            this.VendorId,
            id
          ).subscribe({
            next: ({ presignedUrl, filename }) => {
              window.open(presignedUrl, '_blank');
            }
          });
        } else {
          if (!licenseFile) {
            throw this.ToastService.addErrorToast(
              'Error',
              'License is missing File.',
              { returnError: true }
            );
          }
          const licenseFileParts = licenseFile.split(';base64,');
          const fileType = licenseFileParts[0].replace('data:', '');
          const ArrayBuffer =
            this.UtilityService.convertBase64StringToArrayBuffer(
              licenseFileParts[1]
            );
          window.open(
            URL.createObjectURL(new Blob([ArrayBuffer], { type: fileType })),
            '_blank'
          );
        }
      }
    });
  }

  checkForPassedFindings() {
    if (!this.CompanyVendor) {
      return false;
    }

    const { needsFeeWaiveRenewal } = this.CompanyVendor;

    if (needsFeeWaiveRenewal) {
      return false;
    }

    if (this.VendorTrades.length === 0) {
      return false;
    }

    return this.VendorTrades.every((VendorTrade) => {
      const { PrimaryTradeLicenseRequirements } = VendorTrade;

      if (Array.isArray(PrimaryTradeLicenseRequirements)) {
        return PrimaryTradeLicenseRequirements.every((ptlr) => {
          const { TradeLicenses, isRequired } = ptlr;
          if (Array.isArray(TradeLicenses) && TradeLicenses.length > 0) {
            return TradeLicenses.every((TradeLicense) => {
              const { License } = TradeLicense;

              if (!License) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Trade License is missing License.',
                  { returnError: true }
                );
              }

              const { status, expirationDate, licensorRegion } = License;

              if (!status || !expirationDate || !licensorRegion) {
                return false;
              }

              return (
                status === 'Active' &&
                DateTime.fromJSDate(expirationDate)
                  .diffNow('milliseconds')
                  .valueOf() > 0 &&
                !!licensorRegion
              );
            });
          } else if (isRequired) {
            return false;
          } else {
            return true;
          }
        });
      }

      return true;
    });
  }

  areAllLicensesValid() {
    if (this.ComplianceProgress) {
      const { status } = this.ComplianceProgress;
      return status === 'Passed';
    }

    return this.checkForPassedFindings();
  }

  displayAutoApproveOrExpiringSoonModal() {
    const ExpirationResult = this.checkIfLicenseIsExpiringSoon();
    const { isLicenseExpired, isLicenseExpiringSoon } = ExpirationResult;
    if (isLicenseExpired || isLicenseExpiringSoon) {
      this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'confirm-modal'
      });
      const { componentInstance, result } = this.AutoApproveModal;
      Object.assign(componentInstance, {
        displayText: isLicenseExpired
          ? 'One or more licenses have expired.'
          : 'At least one license is expiring in two weeks or less.',
        hideCancelButton: true
      });

      // VPDW-4199: release the captured modal once this notice is dismissed. Without
      // this the `!this.AutoApproveModal` guard below stays permanently false and the
      // step's approval prompt can never open again for the rest of the page session.
      from(result).subscribe({
        next: () => {
          delete this.AutoApproveModal;
        },
        error: () => {
          delete this.AutoApproveModal;
        }
      });
    } else if (this.LoggedInUser && this.CompanyVendor) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;
      const { viveStatus } = this.CompanyVendor;

      if (
        role &&
        new Set([
          'ADMIN_ADMIN',
          'ADMIN_FRAUD_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]).has(role) &&
        this.checkForPassedFindings() &&
        (this.ComplianceProgress === null ||
          (this.ComplianceProgress &&
            !['Passed', 'Failed'].includes(this.ComplianceProgress.status!))) &&
        viveStatus !== 'Inactive' &&
        !this.AutoApproveModal
      ) {
        this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
          backdrop: 'static',
          modalDialogClass: 'confirm-modal'
        });
        document.body.style.overflowY = 'auto';
        Object.assign(this.AutoApproveModal.componentInstance, {
          // VPDW-4199: scoped to this step, and names any sibling step still needing
          // attention so this never reads as a review-wide all-clear.
          displayText:
            this.ComplianceStepStatusService.buildStepApprovalText(
              'Trades & Licenses'
            )
        });

        const { result } = this.AutoApproveModal;

        from(result).subscribe({
          next: (result) => {
            if (result) {
              delete this.AutoApproveModal;
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({
                  status: 'Passed',
                  type: 'Trades & Licenses'
                })
              );

              if (!new Set(['ADMIN_FRAUD_TEAM']).has(role)) {
                this.SharedDataService.sendEvent('GoToNextStep');
              }
            }
            delete this.AutoApproveModal;
          }
        });
      }
    }
  }

  isLicenseRequirement(VendorTrade: VendorTrade) {
    const { PrimaryTradeLicenseRequirements } = VendorTrade;

    if (
      PrimaryTradeLicenseRequirements &&
      PrimaryTradeLicenseRequirements.length > 0
    ) {
      const { isRequired } = PrimaryTradeLicenseRequirements[0];
      return isRequired;
    }

    return null;
  }

  loadData() {
    this.route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const VendorId = Number(paramMap.get('VendorId'));
        const CompanyVendorId = Number(paramMap.get('CompanyVendorId'));
        const CompanyComplianceTypeId = Number(
          paramMap.get('CompanyComplianceTypeId')
        );

        if (!CompanyId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
            returnError: true
          });
        }

        if (!VendorId) {
          throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
            returnError: true
          });
        }

        if (!CompanyVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor Id.',
            { returnError: true }
          );
        }

        if (!CompanyComplianceTypeId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Compliance Type Id.',
            { returnError: true }
          );
        }

        Object.assign(this, {
          CompanyId,
          VendorId,
          CompanyVendorId,
          CompanyComplianceTypeId
        });

        Object.assign(this.TradesSuppliedGridQuery, { VendorId });

        this.ComplianceService.getClientVendorSharedStates(
          CompanyId,
          VendorId
        ).subscribe({
          next: (ClientVendorSharedStates) => {
            this.SuppliedPrimaryTradeLicenseQueryData = {
              state: { $in: ClientVendorSharedStates },
              SubNationalDivision: {
                attributes: ['id', 'abbreviation']
              },
              VendorTrade: {
                options: { required: true },
                VendorId
              },
              License: {
                attributes: ['id', 'VendorId', 'licenseFileName'],
                VendorId
              },
              PrimaryTrade: {
                attributes: ['id', 'name'],
                options: { required: true },
                TradeSpecificCompliance: {
                  options: { required: false },
                  ComplianceSetting: {
                    options: { required: true },
                    attributes: ['id', 'riskLevel'],
                    CompanyId,
                    CompanyComplianceTypeId
                  }
                }
              }
            };
          }
        });

        this.CompanyVendorService.getCompanyVendorCount({
          CompanyId,
          VendorId,
          isArchived: true
        }).subscribe({
          next: (countOfArchivedClientVendorRelationships) => {
            const companyVendorQuery = {
              attributes: [
                'id',
                'VendorId',
                'CompanyId',
                'CompanyComplianceTypeId'
              ],
              aliasedAttributes: ['clientVendorSharedStates'],
              CompanyId,
              VendorId,
              Company: {
                attributes: ['id', 'legalName', 'hasGlobalCompliance'],
                ComplianceSetting: {
                  options: { required: false },
                  attributes: ['id', 'riskLevel'],
                  riskLevel: 0,
                  CompanyComplianceTypeId: {
                    $eq: {
                      $col: 'CompanyVendor.CompanyComplianceTypeId'
                    }
                  }
                },
                CompanyOperationState: {
                  SubNationalDivision: {
                    attributes: ['abbreviation']
                  }
                }
              },
              Vendor: {
                options: { required: true },
                attributes: ['id', 'country'],
                VendorTradeStates: {
                  SubNationalDivision: {
                    attributes: ['abbreviation']
                  }
                },
                VendorTrade: {
                  options: { required: false },
                  PrimaryTrade: {
                    options: { required: false },
                    attributes: ['id', 'name'],
                    ComplianceSetting: {
                      options: { required: false },
                      attributes: ['id', 'riskLevel'],
                      CompanyId,
                      CompanyComplianceTypeId: {
                        $eq: {
                          $col: 'CompanyVendor.CompanyComplianceTypeId'
                        }
                      }
                    }
                  },
                  PrimaryTradeLicenseRequirement: {
                    options: { required: false },
                    attributes: [
                      'id',
                      'isRequired',
                      'SubNationalDivisionId',
                      'state',
                      'PrimaryTradeId'
                    ],
                    literals: {
                      $and: [
                        {
                          SubNationalDivisionId: {
                            $in: 'clientOperationStates'
                          }
                        },
                        {
                          SubNationalDivisionId: { $in: 'vendorTradeStates' }
                        }
                      ]
                    },
                    PrimaryTrade: {},
                    License: {
                      VendorId,
                      excludedAttributes: ['licenseFile']
                    },
                    SubNationalDivision: {
                      attributes: ['id', 'abbreviation']
                    },
                    TradeLicense: {
                      attributes: [
                        'id',
                        'VendorTradeId',
                        'LicenseId',
                        'PrimaryTradeLicenseRequirementId'
                      ],
                      options: { required: false },
                      VendorTradeId: {
                        $eq: { $col: 'Vendor.VendorTrades.id' }
                      },
                      isNotArchived: { $ne: null },
                      License: {
                        options: { required: false },
                        VendorId,
                        attributes: [
                          'id',
                          'VendorId',
                          'verificationType',
                          'licenseState',
                          'licensorRegion',
                          'issueDate',
                          'expirationDate',
                          'licenseNumber',
                          'status',
                          'classification',
                          'name',
                          'PrimaryTradeId',
                          'state'
                        ]
                      }
                    }
                  }
                }
              },
              // If there is a trade compliance progress with a note, retrieve it.
              TradeLicenseComplianceProgress: {
                options: { required: false },
                Note: {
                  options: { required: false, separate: true }
                }
              }
            };

            if (countOfArchivedClientVendorRelationships > 0) {
              Object.assign(companyVendorQuery, { isArchived: true });
            }

            this.CompanyVendorService.getCompanyVendor(
              this.UtilityService.stringifyObjectsInQueryObject(
                companyVendorQuery
              )
            ).subscribe({
              next: (CompanyVendor: CompanyVendor) => {
                if (!CompanyVendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor information.',
                    { returnError: true }
                  );
                }

                this.CompanyVendor = CompanyVendor;

                const {
                  clientVendorSharedStates,
                  Vendor,
                  Company,
                  TradeLicenseComplianceProgress
                } = CompanyVendor;

                if (!Vendor) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor information.',
                    { returnError: true }
                  );
                }

                if (!Company) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client information.',
                    { returnError: true }
                  );
                }

                this.Company = Company;

                const {
                  hasGlobalCompliance,
                  ComplianceSettings: ClientComplianceSettings,
                  CompanyOperationStates
                } = Company;

                const { country, VendorTrades, VendorTradeStates } = Vendor;

                if (CompanyOperationStates && VendorTradeStates) {
                  this.vendorCompanyTradeStates =
                    clientVendorSharedStates || '';
                }

                if (!country) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor is missing country information.',
                    { returnError: true }
                  );
                }

                this.country = country;

                if (!Array.isArray(VendorTrades) || VendorTrades.length === 0) {
                  this.VendorTrades = [];
                  this.ToastService.addErrorToast(
                    'Info',
                    'Vendor has no trades. Use Choose trades to add one.'
                  );
                  this.SharedDataService.sendEvent('GetReviewAction');
                  return;
                }

                VendorTrades.forEach((VendorTrade) => {
                  const {
                    PrimaryTradeId,
                    PrimaryTrade,
                    PrimaryTradeLicenseRequirements
                  } = VendorTrade;

                  if (!PrimaryTradeId) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Primary Trade Id on Primary Trade.',
                      { returnError: true }
                    );
                  }

                  if (!PrimaryTrade) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Trade information on Vendor Trade',
                      { returnError: true }
                    );
                  }

                  const { ComplianceSettings: PrimaryTradeComplianceSettings } =
                    PrimaryTrade;

                  const ComplianceSettings: ComplianceSetting[] = [];

                  if (
                    PrimaryTradeComplianceSettings &&
                    PrimaryTradeComplianceSettings.length > 0 &&
                    !hasGlobalCompliance
                  ) {
                    ComplianceSettings.push(...PrimaryTradeComplianceSettings);
                  } else if (
                    ClientComplianceSettings &&
                    ClientComplianceSettings.length > 0 &&
                    hasGlobalCompliance
                  ) {
                    ComplianceSettings.push(...ClientComplianceSettings);
                  }

                  if (ComplianceSettings.length !== 1) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Client is missing compliance setting information.',
                      { returnError: true }
                    );
                  }

                  this.PrimaryTradeIdToComplianceSettingMap.set(
                    PrimaryTradeId,
                    ComplianceSettings[0]
                  );

                  if (Array.isArray(PrimaryTradeLicenseRequirements)) {
                    PrimaryTradeLicenseRequirements.forEach((ptlr) => {
                      const {
                        id: PrimaryTradeLicenseRequirementId,
                        TradeLicenses
                      } = ptlr;

                      if (!PrimaryTradeLicenseRequirementId) {
                        throw this.ToastService.addErrorToast(
                          'Error',
                          'License Requirement is missing Id.',
                          { returnError: true }
                        );
                      }

                      if (Array.isArray(TradeLicenses)) {
                        TradeLicenses.forEach((tl) => {
                          const { LicenseId, License } = tl;

                          if (!LicenseId) {
                            throw this.ToastService.addErrorToast(
                              'Error',
                              'Trade License is missing License Id.',
                              { returnError: true }
                            );
                          }

                          if (
                            !this.LicenseIdToPrimaryTradeLicenseRequirementMap.has(
                              LicenseId
                            )
                          ) {
                            this.LicenseIdToPrimaryTradeLicenseRequirementMap.set(
                              LicenseId,
                              new Map()
                            );
                          }

                          if (License) {
                            const { id } = License;

                            if (!id) {
                              throw this.ToastService.addErrorToast(
                                'Error',
                                'License is missing id.',
                                { returnError: true }
                              );
                            }

                            this.LicensesMappedById.set(id, License);
                          }

                          this.LicenseIdToPrimaryTradeLicenseRequirementMap.get(
                            LicenseId
                          )!.set(PrimaryTradeLicenseRequirementId, ptlr);
                        });
                      }
                    });
                  }
                });

                this.VendorTrades = VendorTrades;

                if (TradeLicenseComplianceProgress) {
                  const { Notes } = TradeLicenseComplianceProgress;

                  if (Notes) {
                    const ObservableArray = Notes.map((Note) => {
                      return of(EMPTY).pipe(
                        concatMap(() => {
                          const { id: NoteId, note } = Note;
                          const { componentInstance, result } =
                            this.ModalService.open(ConfirmModalComponent, {
                              backdrop: 'static',
                              size: 'md'
                            });
                          Object.assign(componentInstance, {
                            displayText: note,
                            cancelButtonText: 'Exit',
                            continueButtonText: 'Dismiss'
                          });
                          return from(result).pipe(
                            concatMap((confirm) => {
                              if (confirm) {
                                return this.NoteService.saveNote({
                                  id: NoteId,
                                  ViveProgressId: null
                                });
                              }
                              return of();
                            })
                          );
                        })
                      );
                    });

                    concat(...ObservableArray).subscribe();
                  }
                }

                this.SharedDataService.sendEvent('GetReviewAction');
              }
            });
          }
        });
      }
    });
  }

  ngOnDestroy() {
    // Need to unsubscribe from shared data service when component is destroyed to prevent
    // potential events from firing when next component is instantiated.
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.loadData();
  }
}
